/**
 * YNS Streak System
 * Handles daily study streaks, achievements, XP multipliers, and rewards.
 *
 * Features:
 * - Daily streak tracking with localStorage
 * - Streak milestones (3/7/14/30/60/90 days)
 * - XP multipliers for active streaks
 * - Achievement/badge system
 * - Smart purchase prompts
 * - FOMO elements (social proof)
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    storageKey: 'yns_streaks',
    achievementsKey: 'yns_achievements',
    activityKey: 'yns_activity_log',

    // Streak milestones with rewards
    milestones: [
      { days: 3, name: 'Getting Started', badge: '🌱', xpBonus: 50, reward: null },
      { days: 7, name: 'Week Warrior', badge: '🔥', xpBonus: 100, reward: null },
      { days: 14, name: 'Two Week Champion', badge: '💪', xpBonus: 200, reward: null },
      { days: 30, name: 'Monthly Master', badge: '🏆', xpBonus: 500, reward: { type: 'discount', code: 'STREAK30', percent: 10 } },
      { days: 60, name: 'Dedication Legend', badge: '⭐', xpBonus: 1000, reward: { type: 'discount', code: 'STREAK60', percent: 15 } },
      { days: 90, name: 'Ultimate Scholar', badge: '👑', xpBonus: 2000, reward: { type: 'discount', code: 'STREAK90', percent: 20 } }
    ],

    // XP multipliers based on current streak
    xpMultipliers: [
      { minDays: 0, multiplier: 1.0 },
      { minDays: 3, multiplier: 1.1 },
      { minDays: 7, multiplier: 1.25 },
      { minDays: 14, multiplier: 1.5 },
      { minDays: 30, multiplier: 1.75 },
      { minDays: 60, multiplier: 2.0 }
    ],

    // Social proof messages
    socialProofMessages: [
      '{count} students studying right now',
      '{count} students completed a lesson today',
      '{count} students are on a streak',
      '{count} lessons completed this hour'
    ],

    // Smart prompt triggers
    promptTriggers: {
      rewindCount: 3, // Show prompt after rewinding 3+ times
      pauseDuration: 60, // Show prompt after pausing for 60+ seconds
      categoryCompletion: true, // Show prompt when completing a category
      milestoneReached: true // Show prompt on milestone achievements
    }
  };

  // ============================================
  // State
  // ============================================
  let streakData = null;
  let achievements = [];
  let activityLog = [];

  // ============================================
  // Local Storage
  // ============================================
  function loadStreakData() {
    try {
      const stored = localStorage.getItem(CONFIG.storageKey);
      streakData = stored ? JSON.parse(stored) : {
        currentStreak: 0,
        longestStreak: 0,
        lastStudyDate: null,
        totalStudyDays: 0,
        weeklyActivity: [0, 0, 0, 0, 0, 0, 0], // Sun-Sat
        monthlyActivity: {},
        startDate: new Date().toISOString()
      };
    } catch (e) {
      console.error('Error loading streak data:', e);
      streakData = {
        currentStreak: 0,
        longestStreak: 0,
        lastStudyDate: null,
        totalStudyDays: 0,
        weeklyActivity: [0, 0, 0, 0, 0, 0, 0],
        monthlyActivity: {},
        startDate: new Date().toISOString()
      };
    }
    return streakData;
  }

  function saveStreakData() {
    try {
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(streakData));

      // Sync to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
        window.YNS_SUPABASE.queueSync('streaks', async () => {
          await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.streaks, {
            current_streak: streakData.currentStreak,
            longest_streak: streakData.longestStreak,
            last_study_date: streakData.lastStudyDate,
            total_study_days: streakData.totalStudyDays,
            weekly_activity: streakData.weeklyActivity,
            monthly_activity: streakData.monthlyActivity,
            start_date: streakData.startDate
          }, ['customer_id']);
        });
      }
    } catch (e) {
      console.error('Error saving streak data:', e);
    }
  }

  function loadAchievements() {
    try {
      const stored = localStorage.getItem(CONFIG.achievementsKey);
      achievements = stored ? JSON.parse(stored) : [];
    } catch (e) {
      achievements = [];
    }
    return achievements;
  }

  function saveAchievements() {
    try {
      localStorage.setItem(CONFIG.achievementsKey, JSON.stringify(achievements));

      // Sync to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId) {
        // Sync each achievement individually
        achievements.forEach(achievement => {
          window.YNS_SUPABASE.queueSync(`achievement:${achievement.id}`, async () => {
            await window.YNS_SUPABASE.upsert(window.YNS_SUPABASE.tables.achievements, {
              achievement_id: achievement.id,
              achievement_name: achievement.name,
              badge: achievement.badge,
              xp_bonus: achievement.xpBonus,
              reward: achievement.reward,
              achieved_at: achievement.achievedAt
            }, ['customer_id', 'achievement_id']);
          });
        });
      }
    } catch (e) {
      console.error('Error saving achievements:', e);
    }
  }

  function loadActivityLog() {
    try {
      const stored = localStorage.getItem(CONFIG.activityKey);
      activityLog = stored ? JSON.parse(stored) : [];
      // Keep only last 50 activities
      if (activityLog.length > 50) {
        activityLog = activityLog.slice(-50);
        saveActivityLog();
      }
    } catch (e) {
      activityLog = [];
    }
    return activityLog;
  }

  function saveActivityLog() {
    try {
      localStorage.setItem(CONFIG.activityKey, JSON.stringify(activityLog));

      // Sync latest activity to Supabase
      if (window.YNS_SUPABASE && window.YNS_CONFIG?.customerId && activityLog.length > 0) {
        const latestActivity = activityLog[activityLog.length - 1];
        window.YNS_SUPABASE.queueSync('activity', async () => {
          await window.YNS_SUPABASE.insert(window.YNS_SUPABASE.tables.activityLog, {
            activity_type: latestActivity.type,
            details: latestActivity.details,
            created_at: latestActivity.timestamp
          });
        });
      }
    } catch (e) {
      console.error('Error saving activity log:', e);
    }
  }

  // ============================================
  // Streak Logic
  // ============================================
  function getToday() {
    return new Date().toISOString().split('T')[0];
  }

  function getYesterday() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  function recordStudyActivity(activityType = 'video_watched', details = {}) {
    loadStreakData();

    const today = getToday();
    const yesterday = getYesterday();
    const lastDate = streakData.lastStudyDate;

    // Check if this is a new study day
    if (lastDate !== today) {
      // Update streak
      if (lastDate === yesterday) {
        // Continuing streak
        streakData.currentStreak++;
      } else if (lastDate !== today) {
        // Streak broken or first day
        if (streakData.currentStreak > 0 && lastDate !== null) {
          // Streak was broken
          logActivity('streak_lost', { previousStreak: streakData.currentStreak });
        }
        streakData.currentStreak = 1;
      }

      // Update longest streak
      if (streakData.currentStreak > streakData.longestStreak) {
        streakData.longestStreak = streakData.currentStreak;
      }

      // Update study date
      streakData.lastStudyDate = today;
      streakData.totalStudyDays++;

      // Update weekly activity
      const dayOfWeek = new Date().getDay();
      streakData.weeklyActivity[dayOfWeek]++;

      // Update monthly activity
      const monthKey = today.substring(0, 7); // YYYY-MM
      streakData.monthlyActivity[monthKey] = (streakData.monthlyActivity[monthKey] || 0) + 1;

      saveStreakData();

      // Check for milestone achievements
      checkMilestones();
    }

    // Log the activity
    logActivity(activityType, details);

    // Update UI
    updateStreakUI();

    return streakData;
  }

  function checkMilestones() {
    loadAchievements();

    CONFIG.milestones.forEach(milestone => {
      if (streakData.currentStreak >= milestone.days) {
        const achievementId = `streak_${milestone.days}`;

        // Check if already achieved
        if (!achievements.find(a => a.id === achievementId)) {
          // New milestone reached!
          const achievement = {
            id: achievementId,
            name: milestone.name,
            badge: milestone.badge,
            days: milestone.days,
            xpBonus: milestone.xpBonus,
            reward: milestone.reward,
            achievedAt: new Date().toISOString()
          };

          achievements.push(achievement);
          saveAchievements();

          // Show celebration
          showMilestoneAchievement(achievement);

          // Log activity
          logActivity('milestone_reached', { milestone: milestone.name, days: milestone.days });
        }
      }
    });
  }

  function getXPMultiplier() {
    if (!streakData) loadStreakData();

    let multiplier = 1.0;
    for (const tier of CONFIG.xpMultipliers) {
      if (streakData.currentStreak >= tier.minDays) {
        multiplier = tier.multiplier;
      }
    }
    return multiplier;
  }

  function applyXPMultiplier(baseXP) {
    const multiplier = getXPMultiplier();
    return Math.round(baseXP * multiplier);
  }

  // ============================================
  // Activity Logging
  // ============================================
  function logActivity(type, details = {}) {
    loadActivityLog();

    const activity = {
      type,
      details,
      timestamp: new Date().toISOString()
    };

    activityLog.push(activity);
    saveActivityLog();

    // Dispatch event for activity feed
    document.dispatchEvent(new CustomEvent('yns:activity', {
      detail: activity,
      bubbles: true
    }));
  }

  // ============================================
  // UI Updates
  // ============================================
  function updateStreakUI() {
    if (!streakData) loadStreakData();

    // Update streak badge
    const streakBadge = document.getElementById('streakBadge');
    const streakCount = document.getElementById('streakCount');

    if (streakBadge && streakCount) {
      streakCount.textContent = streakData.currentStreak;

      if (streakData.currentStreak > 0) {
        streakBadge.classList.remove('no-streak');
      } else {
        streakBadge.classList.add('no-streak');
      }

      // Add animation for active streaks
      if (streakData.currentStreak >= 7) {
        streakBadge.classList.add('streak-hot');
      }
    }

    // Update any streak display elements
    document.querySelectorAll('[data-streak-count]').forEach(el => {
      el.textContent = streakData.currentStreak;
    });

    document.querySelectorAll('[data-longest-streak]').forEach(el => {
      el.textContent = streakData.longestStreak;
    });

    document.querySelectorAll('[data-total-days]').forEach(el => {
      el.textContent = streakData.totalStudyDays;
    });

    // Update XP multiplier display
    const multiplier = getXPMultiplier();
    document.querySelectorAll('[data-xp-multiplier]').forEach(el => {
      el.textContent = `${multiplier}x`;
      if (multiplier > 1) {
        el.classList.add('active');
      }
    });
  }

  function showMilestoneAchievement(achievement) {
    // Create and show achievement modal/toast
    const toast = document.createElement('div');
    toast.className = 'yns-achievement-toast';
    toast.innerHTML = `
      <div class="yns-achievement-content">
        <div class="yns-achievement-badge">${achievement.badge}</div>
        <div class="yns-achievement-info">
          <h4>Achievement Unlocked!</h4>
          <p>${achievement.name}</p>
          <span class="yns-achievement-xp">+${achievement.xpBonus} XP Bonus</span>
        </div>
        ${achievement.reward ? `
          <div class="yns-achievement-reward">
            <span>🎁 Reward: ${achievement.reward.percent}% off</span>
            <code>${achievement.reward.code}</code>
          </div>
        ` : ''}
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 500);
    }, 5000);
  }

  // ============================================
  // Social Proof
  // ============================================
  function updateSocialProof() {
    const socialProofEl = document.getElementById('socialProofText');
    if (!socialProofEl) return;

    // Generate realistic-looking numbers
    const baseCount = 30 + Math.floor(Math.random() * 20);
    const timeOfDay = new Date().getHours();

    // Higher numbers during peak study hours (6pm-11pm)
    let count = baseCount;
    if (timeOfDay >= 18 && timeOfDay <= 23) {
      count = baseCount + Math.floor(Math.random() * 30);
    } else if (timeOfDay >= 6 && timeOfDay <= 10) {
      count = baseCount + Math.floor(Math.random() * 15);
    }

    // Select random message
    const messages = CONFIG.socialProofMessages;
    const message = messages[Math.floor(Math.random() * messages.length)];

    socialProofEl.textContent = message.replace('{count}', count);
  }

  function startSocialProofRotation() {
    updateSocialProof();
    // Update every 30 seconds
    setInterval(updateSocialProof, 30000);
  }

  // ============================================
  // Activity Feed
  // ============================================
  function updateActivityFeed() {
    const feedEl = document.getElementById('activityFeed');
    if (!feedEl) return;

    loadActivityLog();

    // Get recent activities
    const recentActivities = activityLog.slice(-10).reverse();

    if (recentActivities.length === 0) {
      feedEl.innerHTML = '<p class="yns-no-activity">Start studying to see your activity!</p>';
      return;
    }

    feedEl.innerHTML = recentActivities.map(activity => {
      const icon = getActivityIcon(activity.type);
      const text = getActivityText(activity);
      const time = getRelativeTime(activity.timestamp);

      return `
        <div class="yns-activity-item">
          <div class="yns-activity-icon">${icon}</div>
          <div class="yns-activity-details">
            <span class="yns-activity-text">${text}</span>
            <span class="yns-activity-time">${time}</span>
          </div>
        </div>
      `;
    }).join('');
  }

  function getActivityIcon(type) {
    const icons = {
      video_watched: '📺',
      video_completed: '✅',
      pdf_opened: '📄',
      annotation_added: '✏️',
      study_session: '📚',
      streak_lost: '💔',
      milestone_reached: '🏆',
      note_added: '📝',
      bookmark_added: '🔖'
    };
    return icons[type] || '📌';
  }

  function getActivityText(activity) {
    const texts = {
      video_watched: `Watched ${activity.details.videoTitle || 'a video'}`,
      video_completed: `Completed ${activity.details.videoTitle || 'a video'}`,
      pdf_opened: `Opened ${activity.details.pdfTitle || 'a PDF'}`,
      annotation_added: 'Added a highlight',
      study_session: 'Started a study session',
      streak_lost: `Lost a ${activity.details.previousStreak}-day streak`,
      milestone_reached: `Reached ${activity.details.milestone}!`,
      note_added: 'Added a note',
      bookmark_added: 'Added a bookmark'
    };
    return texts[activity.type] || 'Activity recorded';
  }

  function getRelativeTime(timestamp) {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  }

  // ============================================
  // Smart Prompts
  // ============================================
  function checkStruggleDetection(videoId, rewindCount, pauseDuration) {
    if (rewindCount >= CONFIG.promptTriggers.rewindCount) {
      showSmartPrompt('struggle', {
        message: 'Having trouble? Our books break down this topic in detail.',
        cta: 'View Books',
        url: '/collections/study-guides'
      });
    }
  }

  function showSmartPrompt(type, options) {
    // Don't show too frequently
    const lastPromptKey = 'yns_last_prompt';
    const lastPrompt = localStorage.getItem(lastPromptKey);
    const cooldownMs = 30 * 60 * 1000; // 30 minutes

    if (lastPrompt && Date.now() - parseInt(lastPrompt) < cooldownMs) {
      return;
    }

    // Create prompt element
    const prompt = document.createElement('div');
    prompt.className = 'yns-smart-prompt';
    prompt.innerHTML = `
      <div class="yns-prompt-content">
        <p>${options.message}</p>
        <div class="yns-prompt-actions">
          <a href="${options.url}" class="yns-prompt-cta">${options.cta}</a>
          <button class="yns-prompt-dismiss">Not now</button>
        </div>
      </div>
    `;

    document.body.appendChild(prompt);

    // Show animation
    requestAnimationFrame(() => prompt.classList.add('show'));

    // Setup dismiss
    prompt.querySelector('.yns-prompt-dismiss').addEventListener('click', () => {
      prompt.classList.remove('show');
      setTimeout(() => prompt.remove(), 300);
    });

    // Record prompt time
    localStorage.setItem(lastPromptKey, Date.now().toString());

    // Auto-hide after 10 seconds
    setTimeout(() => {
      if (document.body.contains(prompt)) {
        prompt.classList.remove('show');
        setTimeout(() => prompt.remove(), 300);
      }
    }, 10000);
  }

  // ============================================
  // Supabase Data Loading
  // ============================================
  async function loadStreakDataFromSupabase() {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.streaks);
      if (rows && rows.length > 0) {
        const serverData = rows[0];
        // Merge with local data - keep highest progress values
        const merged = window.YNS_SUPABASE.mergeHighestProgress(streakData, {
          currentStreak: serverData.current_streak,
          longestStreak: serverData.longest_streak,
          lastStudyDate: serverData.last_study_date,
          totalStudyDays: serverData.total_study_days,
          weeklyActivity: serverData.weekly_activity || [0,0,0,0,0,0,0],
          monthlyActivity: serverData.monthly_activity || {},
          startDate: serverData.start_date,
          updated_at: serverData.updated_at
        });
        streakData = merged;
        localStorage.setItem(CONFIG.storageKey, JSON.stringify(streakData));
        updateStreakUI();
      }
    } catch (e) {
      console.error('[Streak] Error loading from Supabase:', e);
    }
  }

  async function loadAchievementsFromSupabase() {
    if (!window.YNS_SUPABASE || !window.YNS_CONFIG?.customerId) return;

    try {
      const rows = await window.YNS_SUPABASE.select(window.YNS_SUPABASE.tables.achievements);
      if (rows && rows.length > 0) {
        // Transform server data to local format
        const serverAchievements = rows.map(row => ({
          id: row.achievement_id,
          name: row.achievement_name,
          badge: row.badge,
          xpBonus: row.xp_bonus,
          reward: row.reward,
          achievedAt: row.achieved_at
        }));
        // Merge arrays by ID
        achievements = window.YNS_SUPABASE.mergeArraysById(achievements, serverAchievements);
        localStorage.setItem(CONFIG.achievementsKey, JSON.stringify(achievements));
      }
    } catch (e) {
      console.error('[Streak] Error loading achievements from Supabase:', e);
    }
  }

  // ============================================
  // Initialization
  // ============================================
  function init() {
    loadStreakData();
    loadAchievements();
    loadActivityLog();

    // Load from Supabase in background (merge with local)
    setTimeout(() => {
      loadStreakDataFromSupabase();
      loadAchievementsFromSupabase();
    }, 500);

    // Check if streak needs to be reset (missed a day)
    const today = getToday();
    const yesterday = getYesterday();

    if (streakData.lastStudyDate &&
        streakData.lastStudyDate !== today &&
        streakData.lastStudyDate !== yesterday &&
        streakData.currentStreak > 0) {
      // Streak is broken
      logActivity('streak_lost', { previousStreak: streakData.currentStreak });
      streakData.currentStreak = 0;
      saveStreakData();
    }

    // Update UI
    updateStreakUI();
    updateActivityFeed();
    startSocialProofRotation();

    // Listen for study activities
    document.addEventListener('yns:videoWatched', (e) => {
      recordStudyActivity('video_watched', e.detail);
    });

    document.addEventListener('yns:videoCompleted', (e) => {
      recordStudyActivity('video_completed', e.detail);
    });

    document.addEventListener('yns:pdfOpened', (e) => {
      recordStudyActivity('pdf_opened', e.detail);
    });

    document.addEventListener('yns:annotationAdded', (e) => {
      recordStudyActivity('annotation_added', e.detail);
    });

  }

  // ============================================
  // Public API
  // ============================================
  window.YNSStreakSystem = {
    recordActivity: recordStudyActivity,
    getStreakData: () => ({ ...streakData }),
    getXPMultiplier,
    applyXPMultiplier,
    getAchievements: () => [...achievements],
    logActivity,
    updateUI: updateStreakUI,
    updateActivityFeed,
    checkStruggleDetection,
    showSmartPrompt
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// Add achievement toast styles
const achievementStyles = document.createElement('style');
achievementStyles.textContent = `
  .yns-achievement-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    z-index: 100000;
    transform: translateX(150%);
    transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    max-width: 320px;
  }

  .yns-achievement-toast.show {
    transform: translateX(0);
  }

  .yns-achievement-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: #2C2C2C;
  }

  .yns-achievement-badge {
    font-size: 3rem;
    text-align: center;
    animation: bounce-in 0.6s ease-out;
  }

  @keyframes bounce-in {
    0% { transform: scale(0); }
    50% { transform: scale(1.3); }
    100% { transform: scale(1); }
  }

  .yns-achievement-info {
    text-align: center;
  }

  .yns-achievement-info h4 {
    margin: 0 0 4px 0;
    font-size: 1.125rem;
    font-weight: 700;
  }

  .yns-achievement-info p {
    margin: 0 0 8px 0;
    font-size: 0.9375rem;
  }

  .yns-achievement-xp {
    display: inline-block;
    background: rgba(255, 255, 255, 0.3);
    padding: 4px 12px;
    border-radius: 100px;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .yns-achievement-reward {
    background: rgba(255, 255, 255, 0.4);
    padding: 12px;
    border-radius: 8px;
    text-align: center;
  }

  .yns-achievement-reward span {
    display: block;
    font-size: 0.8125rem;
    margin-bottom: 8px;
  }

  .yns-achievement-reward code {
    display: block;
    background: #2C2C2C;
    color: #FFD700;
    padding: 8px 16px;
    border-radius: 4px;
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: 0.1em;
  }

  .yns-smart-prompt {
    position: fixed;
    bottom: 100px;
    right: 20px;
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    z-index: 50000;
    max-width: 300px;
    transform: translateX(150%);
    transition: transform 0.3s ease;
  }

  .yns-smart-prompt.show {
    transform: translateX(0);
  }

  .yns-prompt-content p {
    margin: 0 0 12px 0;
    font-size: 0.9375rem;
    color: #2C2C2C;
  }

  .yns-prompt-actions {
    display: flex;
    gap: 8px;
  }

  .yns-prompt-cta {
    flex: 1;
    padding: 8px 16px;
    background: #46523E;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 600;
    text-decoration: none;
    text-align: center;
    cursor: pointer;
  }

  .yns-prompt-dismiss {
    padding: 8px 12px;
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.8125rem;
    color: #666;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    .yns-achievement-toast {
      top: auto;
      bottom: 80px;
      right: 10px;
      left: 10px;
      max-width: none;
    }

    .yns-smart-prompt {
      bottom: 80px;
      right: 10px;
      left: 10px;
      max-width: none;
    }
  }
`;
document.head.appendChild(achievementStyles);
